import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Wallet, TrendingUp, Shield, Sparkles } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company_name: "NakitFlow Şirketim" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("Hoş geldiniz 👋");
      } else {
        await register(form);
        toast.success("Hesap oluşturuldu, demo veriler yüklendi");
      }
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bir hata oluştu");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: hero */}
      <div className="lg:w-7/12 relative overflow-hidden flex items-center justify-center p-8 lg:p-16">
        <div className="absolute inset-0 nf-grid-pattern opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-emerald-500/5" />
        <div className="relative z-10 max-w-xl nf-fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center nf-glow-cyan">
              <Wallet className="w-6 h-6 text-slate-950" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-head font-extrabold text-2xl tracking-tight">NakitFlow</div>
              <div className="text-xs text-slate-500 font-mono-num uppercase tracking-widest">Finans Komuta Merkezi</div>
            </div>
          </div>

          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-5">
            Nakit akışınız <span className="text-cyan-400">anlık</span>,<br />
            raporlarınız <span className="text-emerald-400">dakikalık</span>.
          </h1>
          <p className="text-slate-400 text-base sm:text-lg mb-10 leading-relaxed max-w-lg">
            Gelirler, giderler, banka hesapları ve cari defterinizi tek yerden yönetin. AI destekli içgörüler ve otomatik fiş okuma dahil.
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, label: "Anlık Nakit Akışı", c: "cyan" },
              { icon: Sparkles, label: "AI İçgörüler + OCR", c: "emerald" },
              { icon: Shield, label: "Çoklu Rol & Döviz", c: "purple" },
            ].map(({ icon: Icon, label, c }) => (
              <div key={label} className="nf-card p-4">
                <Icon className={`w-5 h-5 mb-2 text-${c}-400`} />
                <div className="font-head font-semibold text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:w-5/12 flex items-center justify-center p-8 lg:p-16 bg-[#0A0D14]/40 lg:border-l border-slate-800/60">
        <div className="w-full max-w-md nf-fade-up">
          <h2 className="font-head text-3xl font-bold mb-2">Hesabınıza erişin</h2>
          <p className="text-slate-400 text-sm mb-8">Yeni misiniz? Birkaç saniyede demo verilerle başlayın.</p>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="auth-tabs" className="grid grid-cols-2 w-full bg-slate-900/60 border border-slate-800">
              <TabsTrigger data-testid="tab-login" value="login">Giriş Yap</TabsTrigger>
              <TabsTrigger data-testid="tab-register" value="register">Kayıt Ol</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {tab === "register" && (
                <>
                  <div>
                    <Label>Ad Soyad</Label>
                    <Input data-testid="input-full-name" required value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      placeholder="Zeynep Yılmaz" className="mt-1 bg-slate-900/60 border-slate-800" />
                  </div>
                  <div>
                    <Label>Şirket Adı</Label>
                    <Input data-testid="input-company" value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      className="mt-1 bg-slate-900/60 border-slate-800" />
                  </div>
                </>
              )}
              <div>
                <Label>E-posta</Label>
                <Input data-testid="input-email" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="siz@sirket.com" className="mt-1 bg-slate-900/60 border-slate-800" />
              </div>
              <div>
                <Label>Şifre</Label>
                <Input data-testid="input-password" required type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••" className="mt-1 bg-slate-900/60 border-slate-800" />
              </div>
              <Button data-testid="submit-auth" type="submit" disabled={busy}
                className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-slate-950 font-semibold h-11">
                {busy ? "Lütfen bekleyin..." : tab === "login" ? "Giriş Yap" : "Hesap Oluştur"}
              </Button>
            </form>
          </Tabs>

          <div className="mt-6 text-xs text-slate-500 text-center">
            Devam ederek <Link to="#" className="text-cyan-400 hover:underline">Kullanım Şartları</Link>&apos;nı kabul edersiniz.
          </div>
        </div>
      </div>
    </div>
  );
}
